import { useState, useEffect, useCallback, useMemo } from "react";
import { literals } from "@/constants/literals";
import "../../styles/InspectionsTable.css";
import {
  getImageDeletionRequestsByStatus,
  getImageUrl,
} from "@/lib/services/images";
import { appToast } from "@/lib/toast";
import { useLoader } from "@/store/useLoader";
import { Icon } from "@/components/icons/Icon";
import { formatDateDDMmmYYYY } from "@/lib/dates";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import Modal from "@/components/modal/Modal";
import type { PetitionsParams, RequestPhotoInfo } from "@/types/types.d";
import RequestDeleteModalContent from "./request-page-components/RequestDeleteModalContent";
import InputSelect from "@/components/input-select/InputSelect";
import { PUESTO_OPTIONS } from "@/constants/selectsLists";
import RequestEditionModalContent from "./request-page-components/RequestEditionModalContent";
import Pagination from "@/components/Pagination/Pagination";

const RequestType = ({ row }: { row: RequestPhotoInfo }) => {
  if (row.editionRequest && row.editionRequest.length > 0) {
    return (
      <td className="px-2 py-3 text-red-600">
        <Icon name="pencil" className="size-4 opacity-60" />
      </td>
    );
  }
  return (
    <td className="px-2 py-3 text-red-600">
      <Icon name="remove" className="size-4 opacity-60" />
    </td>
  );
};

const Requests = () => {
  const [openRequestModal, setOpenRequestModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<RequestPhotoInfo | null>(
    null,
  );
  const [requests, setRequests] = useState<RequestPhotoInfo[]>([]);
  const [rows, setRows] = useState<RequestPhotoInfo[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [photoURL, setPhotoURL] = useState<string>("");
  const [areaForSelect, setAreaForSelect] = useState("");
  const [area, setArea] = useState("");
  const [confirmModal, setConfirmModal] = useState(false);
  const [lastPage, setLastPage] = useState(0);
  const [page, setPage] = useState(0);

  const { upLoader, downLoader } = useLoader();

  const areaSelectList = useMemo(() => {
    const filtered = PUESTO_OPTIONS.filter((option) => option.value !== "");
    return [{ label: literals.all, value: "all" }, ...filtered];
  }, []);

  const loadRequests = useCallback(
    async (newPage?: number) => {
      try {
        upLoader();
        const requestParams: PetitionsParams = {
          status: "PENDING",
          page: newPage || newPage === 0 ? newPage : page,
        };
        if (area && area.length > 0) requestParams.area = area;

        const tableData = await getImageDeletionRequestsByStatus(requestParams);

        const mapped: RequestPhotoInfo[] = tableData.content.map((req) => ({
          ...req,
          fileName: req.s3Key.split("/").pop() ?? req.s3Key,
        }));

        setPage(tableData?.pageable?.pageNumber || 0);
        setLastPage(tableData?.totalPages || 1);
        setRequests(mapped);
        setRows(mapped);
      } catch {
        appToast.error(literals.aProblem, literals.getInfoError);
      } finally {
        downLoader();
        setLoaded(true);
      }
    },
    [area, downLoader, upLoader, page],
  );

  useEffect(() => {
    loadRequests();
  }, [area, loadRequests]);

  const selectArea = (newArea: string) => {
    if (newArea !== areaForSelect) {
      setArea(newArea === "all" ? "" : newArea);
      setPage(0);
      setAreaForSelect(newArea);
    }
  };

  useEffect(() => {
    const loadImage = async () => {
      if (!selectedPhoto) return;

      try {
        const url = await getImageUrl(selectedPhoto.imageId);
        setPhotoURL(url);
      } catch (e) {
        console.error("Error loading image", e);
        setPhotoURL("");
      }
    };

    loadImage();
  }, [selectedPhoto]);

  const changePage = (newPage: number) => {
    setPage(newPage);
    loadRequests(newPage);
  };

  return (
    <div data-testid="photos-request-page w-full">
      <div className="mb-4 w-56">
        <InputSelect
          size="small"
          label={literals.workstation}
          id="photos-puesto"
          name="photos-puesto"
          options={areaSelectList}
          value={area}
          onChange={selectArea}
          placeholder={literals.workstation}
        />
      </div>

      <div className="mt-4 border border-l border-ib-gray-lightest rounded-[8px] bg-white">
        <table className="w-full">
          <thead>
            <tr className="text-left text-sm">
              <th className="central-header border-b border-b-ib-gray-lightest">
                {literals.requestDate}
              </th>
              <th className="central-header border-b border-b-ib-gray-lightest">
                {literals.type}
              </th>
              <th className="central-header border-b border-b-ib-gray-lightest">
                {literals.workstation}
              </th>
              <th className="central-header border-b border-b-ib-gray-lightest">
                {literals.step}
              </th>
              <th className="central-header border-b border-b-ib-gray-lightest">
                {literals.name}
              </th>
              <th className="central-header border-b border-b-ib-gray-lightest">
                {literals.description}
              </th>
              <th className="central-header border-b border-b-ib-gray-lightest">
                {literals.visibleClient}
              </th>
              <th className="central-header border-b border-b-ib-gray-lightest"></th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={row.requestId}
                className={`${idx % 2 === 0 ? "table-even-row" : "table-odd-row"} ${
                  idx === rows.length - 1 ? "last-row" : ""
                } h-16 border-b border-b-ib-gray-lightest text-sm`}
              >
                <td className="px-2 py-3">
                  {formatDateDDMmmYYYY(row.createdAt)}
                </td>
                <RequestType row={row} />
                <td className="px-2 py-3">{row.area}</td>
                <td className="px-2 py-3">{row.step}</td>
                <td className="px-2 py-3">{row.fileName}</td>
                <td className="px-2 py-3 truncate max-w-[520px]">
                  {row.imageGroupDescription}
                </td>
                <td className="px-2 py-3">
                  {row.imageGroupVisible ? "Sí" : "No"}
                </td>

                <td className="px-2 py-3">
                  <SecondaryButton
                    size="medium"
                    label={literals.respondRequest}
                    colour="red"
                    onClick={() => {
                      const full =
                        requests.find((r) => r.requestId === row.requestId) ??
                        null;
                      if (full) setSelectedPhoto(full);
                      setOpenRequestModal(true);
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lastPage > 1 ? (
        <div className="py-4">
          <Pagination
            lastPage={lastPage}
            currentPage={page + 1}
            changePage={changePage}
          />
        </div>
      ) : null}

      {selectedPhoto && (
        <Modal
          opened={openRequestModal}
          title={
            confirmModal
              ? literals.saveChanges
              : (selectedPhoto.s3Key.split("/").pop() ?? selectedPhoto.s3Key)
          }
          onClose={() => setOpenRequestModal(false)}
        >
          {!selectedPhoto.editionRequest ||
          selectedPhoto.editionRequest.length === 0 ? (
            <RequestDeleteModalContent
              photoURL={photoURL}
              image={selectedPhoto}
              loadRequests={loadRequests}
              closeModal={() => setOpenRequestModal(false)}
              deselectPhoto={() => setSelectedPhoto(null)}
            />
          ) : (
            <RequestEditionModalContent
              photoURL={photoURL}
              image={selectedPhoto}
              loadRequests={loadRequests}
              closeModal={() => setOpenRequestModal(false)}
              deselectPhoto={() => setSelectedPhoto(null)}
              showConfirmModal={setConfirmModal}
            />
          )}
        </Modal>
      )}

      {loaded && rows.length === 0 && (
        <div className="py-8 text-center text-sm text-gray-500">
          {literals.noPenndingRequests}
        </div>
      )}
    </div>
  );
};

export default Requests;
